// Settings Page Component - Refactored with extracted tab components
import { useState, useEffect } from "react";
import axios from "axios";
import {
  RefreshCw, Bell, Key, Link, Users, Shield, Clock, Activity,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API } from "./constants";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

// Extracted tab components
import SettingsSyncTab from "./SettingsSyncTab";
import SettingsAlertsTab from "./SettingsAlertsTab";
import SettingsCredentialsTab from "./SettingsCredentialsTab";
import SettingsIntegrationsTab from "./SettingsIntegrationsTab";
import SettingsAccessCodesTab from "./SettingsAccessCodesTab";
import SettingsDiagnosticsTab from "./SettingsDiagnosticsTab";
import DateManagement from "./DateManagement";
import AccountManagement from "./AccountManagement";
import FeatureAccessControl from "./FeatureAccessControl";

const SettingsPage = ({ lang = "en" }) => {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("sync");
  const [badgeCounts, setBadgeCounts] = useState({ alerts: 0, credentials: 0, accessCodes: 0, syncRunning: false, ytConnected: false, defaultPeriod: 7 });

  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const [alertsRes, credsRes, syncRes, ytRes, periodRes, codesRes] = await Promise.all([
          axios.get(`${API}/alerts`).catch(() => ({ data: [] })),
          axios.get(`${API}/settings/credentials`).catch(() => ({ data: [] })),
          axios.get(`${API}/jobs/status`).catch(() => ({ data: {} })),
          axios.get(`${API}/youtube/status`).catch(() => ({ data: { configured: false } })),
          axios.get(`${API}/settings/default-period`).catch(() => ({ data: { period_days: 7 } })),
          isAdmin ? axios.get(`${API}/auth/codes`).catch(() => ({ data: { codes: [] } })) : Promise.resolve({ data: { codes: [] } }),
        ]);
        setBadgeCounts({
          alerts: Array.isArray(alertsRes.data) ? alertsRes.data.length : 0,
          credentials: Array.isArray(credsRes.data) ? credsRes.data.length : 0,
          accessCodes: (codesRes.data.codes || []).length,
          syncRunning: syncRes.data?.scheduler_running || false,
          ytConnected: ytRes.data?.configured || false,
          defaultPeriod: periodRes.data?.period_days || 7,
        });
      } catch {}
    };
    fetchBadges();
  }, [isAdmin]);

  const settingsHomeCards = [
    { key: "sync", icon: RefreshCw, title: lang === "ko" ? "동기화" : lang === "zh" ? "同步" : "Daily Sync", description: lang === "ko" ? "스케줄러, 기본 기간, API 상태를 관리합니다" : lang === "zh" ? "管理调度器、默认周期和API健康状态" : "Manage scheduler, default period, and API health", badge: badgeCounts.syncRunning ? (lang === "ko" ? "실행 중" : "Running") : (lang === "ko" ? "중지됨" : "Stopped") },
    { key: "alerts", icon: Bell, title: lang === "ko" ? "알림" : lang === "zh" ? "通知" : "Alerts", description: lang === "ko" ? "임계치 알림과 알림 이력을 관리합니다" : lang === "zh" ? "管理阈值通知和通知历史" : "Manage threshold alerts and alert history", badge: `${badgeCounts.alerts}` },
    { key: "credentials", icon: Key, title: lang === "ko" ? "자격증명" : lang === "zh" ? "凭证" : "Credentials", description: lang === "ko" ? "플랫폼 API 키와 토큰을 관리합니다" : lang === "zh" ? "管理平台API密钥和令牌" : "Manage platform API keys and tokens", badge: `${badgeCounts.credentials}` },
    { key: "integrations", icon: Link, title: lang === "ko" ? "연동" : lang === "zh" ? "集成" : "Integrations", description: lang === "ko" ? "유튜브, 이메일, 쿠키/프록시 연동 상태를 확인합니다" : lang === "zh" ? "查看YouTube、邮件、爬虫和代理集成状态" : "Review YouTube, email, crawler, and proxy integrations", badge: badgeCounts.ytConnected ? (lang === "ko" ? "연결됨" : "Connected") : (lang === "ko" ? "확인 필요" : "Check") },
    { key: "artist-groups", type: "route", icon: Users, title: lang === "ko" ? "아티스트 그룹" : lang === "zh" ? "艺人分组" : "Artist Groups", description: lang === "ko" ? "설정 섹션 안에서 그룹 묶음/자동 매칭을 관리합니다" : lang === "zh" ? "管理分组账户和自动匹配" : "Manage grouped artist accounts and auto-matching", badge: lang === "ko" ? "바로가기" : "Open" },
    { key: "date-mgmt", icon: Clock, title: lang === "ko" ? "날짜 관리" : lang === "zh" ? "日期管理" : "Date Management", description: lang === "ko" ? "데이터 날짜 기준과 운영 기간을 관리합니다" : lang === "zh" ? "管理日期控制和运营窗口" : "Manage date controls and operating windows", badge: `${badgeCounts.defaultPeriod}${lang === "ko" ? "일" : "d"}` },
    { key: "debug", icon: Activity, title: lang === "ko" ? "디버그" : lang === "zh" ? "调试" : "Debug", description: lang === "ko" ? "진단 및 운영 상태를 점검합니다" : lang === "zh" ? "检查诊断和运营状态" : "Inspect diagnostics and operational status", badge: lang === "ko" ? "진단" : "Diagnostics", adminOnly: true },
    { key: "access-codes", icon: Shield, title: lang === "ko" ? "접근 코드" : lang === "zh" ? "访问码" : "Access Codes", description: lang === "ko" ? "사용자 액세스 코드와 로그인 이력을 관리합니다" : lang === "zh" ? "管理访问码和登录历史" : "Manage access codes and login history", badge: `${badgeCounts.accessCodes}`, adminOnly: true },
  ].filter((card) => !card.adminOnly || isAdmin);

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="settings-page">
      {/* User Info Card */}
      <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                {isAdmin ? <Shield className="h-5 w-5 text-primary" /> : <Users className="h-5 w-5 text-primary" />}
              </div>
              <div className="min-w-0">
                <p className="truncate font-medium">{user?.name || "User"}</p>
                <Badge variant={isAdmin ? "default" : "secondary"} className="mt-1">
                  {isAdmin ? (lang === "ko" ? "관리자" : "Admin") : (lang === "ko" ? "일반 사용자" : "User")}
                </Badge>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={logout} className="w-full sm:w-auto" data-testid="settings-logout-btn">
              {lang === "ko" ? "로그아웃" : lang === "zh" ? "退出" : "Logout"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Settings Home Cards */}
      <Card data-testid="settings-home-card-grid" className="border-border/60 bg-gradient-to-b from-primary/6 via-background to-background">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-heading">{lang === "ko" ? "설정 홈" : lang === "zh" ? "设置首页" : "Settings Home"}</CardTitle>
          <CardDescription>{lang === "ko" ? "상단 카드에서 원하는 설정 영역으로 바로 이동할 수 있습니다." : lang === "zh" ? "使用下方卡片直接跳转到各设置区域。" : "Use the cards below to jump directly into each settings area."}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {settingsHomeCards.map((card) => {
            const Icon = card.icon;
            const isActive = !card.type && activeTab === card.key;
            return (
              <button
                key={card.key}
                type="button"
                onClick={() => { if (card.type === "route") { navigate("/settings/artist-groups"); return; } setActiveTab(card.key); }}
                className={`min-w-0 rounded-3xl border p-4 text-left transition-colors ${isActive ? "border-primary/40 bg-primary/10" : "border-border/60 bg-background/80 hover:border-primary/20 hover:bg-muted/40"}`}
                data-testid={`settings-home-card-${card.key}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="rounded-2xl border border-border/60 bg-muted/40 p-2"><Icon className="h-4 w-4 text-primary" /></div>
                  <Badge variant="outline" className="shrink-0" data-testid={`settings-home-card-badge-${card.key}`}>{card.badge}</Badge>
                </div>
                <div className="mt-4 space-y-1">
                  <p className="text-sm font-semibold text-foreground">{card.title}</p>
                  <p className="text-xs leading-5 text-muted-foreground">{card.description}</p>
                </div>
              </button>
            );
          })}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="overflow-x-auto pb-1" data-testid="settings-tabs-scroll">
          <TabsList className="inline-flex h-auto min-w-full items-stretch justify-start gap-2 rounded-2xl bg-muted/30 p-1">
            <TabsTrigger value="sync" className="shrink-0 whitespace-nowrap data-[state=active]:bg-card" data-testid="settings-tab-sync">Daily Sync</TabsTrigger>
            <TabsTrigger value="alerts" className="shrink-0 whitespace-nowrap data-[state=active]:bg-card" data-testid="settings-tab-alerts">Alerts</TabsTrigger>
            <TabsTrigger value="credentials" className="shrink-0 whitespace-nowrap data-[state=active]:bg-card" data-testid="settings-tab-credentials">Credentials</TabsTrigger>
            <TabsTrigger value="integrations" className="shrink-0 whitespace-nowrap data-[state=active]:bg-card" data-testid="settings-tab-integrations">Integrations</TabsTrigger>
            <TabsTrigger value="debug" className="shrink-0 whitespace-nowrap data-[state=active]:bg-card" data-testid="debug-tab">Debug</TabsTrigger>
            <TabsTrigger value="date-mgmt" className="shrink-0 whitespace-nowrap data-[state=active]:bg-card" data-testid="date-mgmt-tab">Date Management</TabsTrigger>
            {isAdmin && <TabsTrigger value="access-codes" className="shrink-0 whitespace-nowrap data-[state=active]:bg-card" data-testid="settings-tab-access-codes"><Key className="h-4 w-4 mr-1" />Access Codes</TabsTrigger>}
            {isAdmin && <TabsTrigger value="account-mgmt" className="shrink-0 whitespace-nowrap data-[state=active]:bg-card" data-testid="settings-tab-account-mgmt"><Users className="h-4 w-4 mr-1" />{lang === "ko" ? "계정 관리" : lang === "zh" ? "账户管理" : "Accounts"}</TabsTrigger>}
            {isAdmin && <TabsTrigger value="feature-access" className="shrink-0 whitespace-nowrap data-[state=active]:bg-card" data-testid="settings-tab-feature-access"><Shield className="h-4 w-4 mr-1" />{lang === "ko" ? "기능 접근" : lang === "zh" ? "功能权限" : "Feature Access"}</TabsTrigger>}
          </TabsList>
        </div>

        <TabsContent value="sync" className="space-y-4 mt-4"><SettingsSyncTab lang={lang} isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="alerts" className="space-y-4 mt-4"><SettingsAlertsTab lang={lang} /></TabsContent>
        <TabsContent value="credentials" className="space-y-4 mt-4"><SettingsCredentialsTab lang={lang} /></TabsContent>
        <TabsContent value="integrations" className="space-y-4 mt-4"><SettingsIntegrationsTab lang={lang} isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="debug" className="space-y-4 mt-4" data-testid="debug-panel"><SettingsDiagnosticsTab lang={lang} /></TabsContent>
        <TabsContent value="date-mgmt" className="space-y-4 mt-4" data-testid="date-mgmt-panel"><DateManagement lang={lang} /></TabsContent>
        {isAdmin && <TabsContent value="access-codes" className="space-y-4 mt-4"><SettingsAccessCodesTab lang={lang} /></TabsContent>}
        {isAdmin && <TabsContent value="account-mgmt" className="space-y-4 mt-4" data-testid="account-mgmt-panel"><AccountManagement lang={lang} /></TabsContent>}
        {isAdmin && <TabsContent value="feature-access" className="space-y-4 mt-4" data-testid="feature-access-panel"><FeatureAccessControl lang={lang} /></TabsContent>}
      </Tabs>
    </div>
  );
};

export default SettingsPage;
